/* @flow */
import invariant from 'invariant';
import * as React from 'react';
import ReactDOM from 'react-dom';

import findScrollParent from 'utils/findScrollParent';

/* Component for monitoring and managing callbacks for the visibility of a list
 * item within a scrollable view.
 *
 * Note: If you're looking for a component to load more items in a list, you
 * might want to consider ScrollLoadZone instead.
 *
 * Usage:
 * <ul>
 *   ...
 *   <li>
 *     <ScrollWatch threshold={0.2} onVisibilityChange={this.itemVisibilityChanged}>
 *       {renderItem()}
 *     </ScrollWatch>
 *   </li>
 *   ...
 * </ul>
 */

class ScrollWatch extends React.Component<{
  onEnter?: Function,
  onLeave?: Function,
  onVisibilityChange?: Function,
  threshold: number,
  children: React.Node,
}> {
  static defaultProps = {
    threshold: 0,
  };

  componentDidMount() {
    this._scrollParent = findScrollParent(ReactDOM.findDOMNode(this));
    // TODO: Attach a single event listener for all instances?
    this._scrollParent.addEventListener('scroll', this.handleScroll, false);
    window.addEventListener('resize', this.handleScroll, false);
  }

  componentWillUnmount() {
    if (this._scrollParent) {
      this._scrollParent.removeEventListener('scroll', this.handleScroll, false);
    }
    window.removeEventListener('resize', this.handleScroll, false);
  }

  _scrollParent = null;
  _previousVisiblity = '';

  getVisibility = () => {
    const node = ReactDOM.findDOMNode(this);
    invariant(
      node instanceof window.HTMLElement,
      'ScrollWatch findDOMNode returned non HTMLElement'
    );
    const top = this.distanceToTopOfScrollParent(node);
    const height = node.offsetHeight;

    let parentHeight;
    let parentScrollTop;

    if (this._scrollParent === window) {
      parentHeight = window.innerHeight;
      parentScrollTop = window.pageYOffset;
    } else if (this._scrollParent) {
      parentHeight = this._scrollParent.offsetHeight;
      parentScrollTop = this._scrollParent.scrollTop;
    }

    const threshold = (parentHeight || 0) * (this.props.threshold || 0);

    if (top + height + threshold < parentScrollTop) {
      return 'above';
    } else if (top - threshold > parentScrollTop + parentHeight) {
      return 'below';
    } else {
      return 'visible';
    }
  };

  distanceToTopOfScrollParent = (node: HTMLElement) => {
    if (this._scrollParent !== window && !node.offsetParent) {
      throw new Error(
        'ScrollWatch requires a scroll parent with `position: relative` in order to calculate ' +
          'it’s visibility.'
      );
    }
    if (
      node.offsetParent === this._scrollParent ||
      !node.offsetParent ||
      !(node.offsetParent instanceof HTMLElement)
    ) {
      return node.offsetTop;
    } else {
      return node.offsetTop + this.distanceToTopOfScrollParent(node.offsetParent);
    }
  };
  handleScroll = () => {
    const visibility = this.getVisibility();
    if (this._previousVisiblity === visibility) {
      return;
    }
    if (visibility === 'visible') {
      if (this.props.onEnter) {
        this.props.onEnter();
      }
    } else {
      if (this.props.onLeave) {
        this.props.onLeave();
      }
    }
    if (this.props.onVisibilityChange) {
      this.props.onVisibilityChange(visibility, this._previousVisiblity);
    }
    this._previousVisiblity = visibility;
  };
  isVisible = () => {
    const visibility = this._previousVisiblity || this.getVisibility();
    return visibility === 'visible';
  };
  render() {
    return React.Children.only(this.props.children);
  }
}
export default ScrollWatch;
